/* eslint-disable fp/no-mutating-methods */
import {
  createAsyncThunk,
  createSlice as _createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import type {
  Book,
  CreateBookRequest,
  UpdateBookRequest,
  UpdatePublicationStatusRequest,
  Chapter,
  CreateChapterRequest,
  UpdateChapterRequest,
  ReorderChaptersRequest,
  Category,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  BookCategoryAssociation,
  AssignCategoryRequest,
  Contributor,
  CreateContributorRequest,
  UpdateContributorRequest,
  ChapterContributorAssociation,
  AssignChapterContributorRequest,
  PublishChapterResponse,
} from './types'

let BASE_URL = '/api/codex'

// Async Thunks - Books
export let fetchBooks = createAsyncThunk('codex/fetchBooksStatus', () =>
  axios.get<Book[]>(`${BASE_URL}/books`).then(pluckData),
)

export let fetchBookByID = createAsyncThunk(
  'codex/fetchBookByIDStatus',
  (id: string) => axios.get<Book>(`${BASE_URL}/books/${id}`).then(pluckData),
)

export let createBook = createAsyncThunk(
  'codex/createBookStatus',
  (data: CreateBookRequest) =>
    axios.post<Book>(`${BASE_URL}/books`, data).then(pluckData),
)

export let updateBook = createAsyncThunk(
  'codex/updateBookStatus',
  ({ id, data }: { id: string; data: UpdateBookRequest }) =>
    axios.put<Book>(`${BASE_URL}/books/${id}`, data).then(pluckData),
)

export let updatePublicationStatus = createAsyncThunk(
  'codex/updatePublicationStatusStatus',
  ({ id, data }: { id: string; data: UpdatePublicationStatusRequest }) =>
    axios
      .put<Book>(`${BASE_URL}/books/${id}/publication-status`, data)
      .then(pluckData),
)

export let deleteBook = createAsyncThunk(
  'codex/deleteBookStatus',
  async (id: string) => {
    await axios.delete(`${BASE_URL}/books/${id}`)
    return id
  },
)

export let republishBook = createAsyncThunk(
  'codex/republishBookStatus',
  async (id: string) => {
    await axios.post(`${BASE_URL}/books/${id}/republish`)
    return id
  },
)

// Async Thunks - Book Chapters
export let fetchBookChapters = createAsyncThunk(
  'codex/fetchBookChaptersStatus',
  (bookID: string) =>
    axios
      .get<Chapter[]>(`${BASE_URL}/books/${bookID}/chapters`)
      .then(pluckData),
)

export let createChapter = createAsyncThunk(
  'codex/createChapterStatus',
  ({ bookID, data }: { bookID: string; data: CreateChapterRequest }) =>
    axios
      .post<Chapter>(`${BASE_URL}/books/${bookID}/chapters`, data)
      .then(pluckData),
)

export let reorderChapters = createAsyncThunk(
  'codex/reorderChaptersStatus',
  ({ bookID, data }: { bookID: string; data: ReorderChaptersRequest }) =>
    axios
      .put<string>(`${BASE_URL}/books/${bookID}/chapters/reorder`, data)
      .then(pluckData),
)

// Async Thunks - Chapters
export let updateChapter = createAsyncThunk(
  'codex/updateChapterStatus',
  ({ id, data }: { id: string; data: UpdateChapterRequest }) =>
    axios.put<Chapter>(`${BASE_URL}/chapters/${id}`, data).then(pluckData),
)

export let deleteChapter = createAsyncThunk(
  'codex/deleteChapterStatus',
  async (id: string) => {
    await axios.delete(`${BASE_URL}/chapters/${id}`)
    return id
  },
)

// Async Thunks - Categories
export let fetchCategories = createAsyncThunk(
  'codex/fetchCategoriesStatus',
  () => axios.get<Category[]>(`${BASE_URL}/categories`).then(pluckData),
)

export let fetchCategoryByID = createAsyncThunk(
  'codex/fetchCategoryByIDStatus',
  (id: string) =>
    axios.get<Category>(`${BASE_URL}/categories/${id}`).then(pluckData),
)

export let createCategory = createAsyncThunk(
  'codex/createCategoryStatus',
  (data: CreateCategoryRequest) =>
    axios.post<Category>(`${BASE_URL}/categories`, data).then(pluckData),
)

export let updateCategory = createAsyncThunk(
  'codex/updateCategoryStatus',
  ({ id, data }: { id: string; data: UpdateCategoryRequest }) =>
    axios.put<Category>(`${BASE_URL}/categories/${id}`, data).then(pluckData),
)

export let deleteCategory = createAsyncThunk(
  'codex/deleteCategoryStatus',
  async (id: string) => {
    await axios.delete(`${BASE_URL}/categories/${id}`)
    return id
  },
)

// Async Thunks - Book-Category Associations
export let fetchBookCategories = createAsyncThunk(
  'codex/fetchBookCategoriesStatus',
  (bookID: string) =>
    axios
      .get<Category[]>(`${BASE_URL}/books/${bookID}/categories`)
      .then(pluckData),
)

export let assignCategoryToBook = createAsyncThunk(
  'codex/assignCategoryToBookStatus',
  ({ bookID, data }: { bookID: string; data: AssignCategoryRequest }) =>
    axios
      .post<BookCategoryAssociation>(
        `${BASE_URL}/books/${bookID}/categories`,
        data,
      )
      .then(pluckData),
)

export let removeCategoryFromBook = createAsyncThunk(
  'codex/removeCategoryFromBookStatus',
  async ({ bookID, categoryID }: { bookID: string; categoryID: string }) => {
    await axios.delete(`${BASE_URL}/books/${bookID}/categories/${categoryID}`)
    return categoryID
  },
)

// Async Thunks - Contributors
export let fetchContributors = createAsyncThunk(
  'codex/fetchContributorsStatus',
  () => axios.get<Contributor[]>(`${BASE_URL}/contributors`).then(pluckData),
)

export let fetchContributorByID = createAsyncThunk(
  'codex/fetchContributorByIDStatus',
  (id: string) =>
    axios.get<Contributor>(`${BASE_URL}/contributors/${id}`).then(pluckData),
)

export let createContributor = createAsyncThunk(
  'codex/createContributorStatus',
  (data: CreateContributorRequest) =>
    axios.post<Contributor>(`${BASE_URL}/contributors`, data).then(pluckData),
)

export let updateContributor = createAsyncThunk(
  'codex/updateContributorStatus',
  ({ id, data }: { id: string; data: UpdateContributorRequest }) =>
    axios
      .put<Contributor>(`${BASE_URL}/contributors/${id}`, data)
      .then(pluckData),
)

export let deleteContributor = createAsyncThunk(
  'codex/deleteContributorStatus',
  async (id: string) => {
    await axios.delete(`${BASE_URL}/contributors/${id}`)
    return id
  },
)

// Async Thunks - Chapter Contributors
export let fetchChapterContributors = createAsyncThunk(
  'codex/fetchChapterContributorsStatus',
  (chapterID: string) =>
    axios
      .get<Contributor[]>(`${BASE_URL}/chapters/${chapterID}/contributors`)
      .then(pluckData),
)

export let assignContributorToChapter = createAsyncThunk(
  'codex/assignContributorToChapterStatus',
  ({
    chapterID,
    data,
  }: {
    chapterID: string
    data: AssignChapterContributorRequest
  }) =>
    axios
      .post<ChapterContributorAssociation>(
        `${BASE_URL}/chapters/${chapterID}/contributors`,
        data,
      )
      .then(pluckData),
)

export let removeContributorFromChapter = createAsyncThunk(
  'codex/removeContributorFromChapterStatus',
  async ({
    chapterID,
    contributorID,
  }: {
    chapterID: string
    contributorID: string
  }) => {
    await axios.delete(
      `${BASE_URL}/chapters/${chapterID}/contributors/${contributorID}`,
    )
    return contributorID
  },
)

// Async Thunks - Chapter Publish
export let publishChapter = createAsyncThunk(
  'codex/publishChapterStatus',
  (chapterID: string) =>
    axios
      .post<PublishChapterResponse>(`${BASE_URL}/chapters/${chapterID}/publish`)
      .then(pluckData),
)

export let unpublishChapter = createAsyncThunk(
  'codex/unpublishChapterStatus',
  async (chapterID: string) => {
    await axios.delete(`${BASE_URL}/chapters/${chapterID}/publish`)
  },
)

export let getContributorProfileImageUploadUrl = async (
  id: string,
): Promise<{ url: string }> => {
  let url = await axios
    .put<string>(`${BASE_URL}/contributors/${id}/profile-image`)
    .then(pluckData)
  return { url }
}

// Upload Thumbnail
export let getBookThumbnailUploadUrl = async (
  id: string,
): Promise<{ url: string }> => {
  let url = await axios
    .put<string>(`${BASE_URL}/books/${id}/thumbnail`)
    .then(pluckData)
  return { url }
}

// State
type CodexState = {
  booksStatus: Status
  books: Book[]
  createBookStatus: Status
  updateBookStatus: Status
  deleteBookStatus: Status
  republishBookStatus: Status
  activeBookStatus: Status
  activeBook: Book | null
  newlyCreatedBookID: string | null
  deletedBookID: string | null
  chaptersStatus: Status
  chapters: Chapter[]
  categoriesStatus: Status
  categories: Category[]
  createCategoryStatus: Status
  newlyCreatedCategoryID: string | null
  updateCategoryStatus: Status
  deleteCategoryStatus: Status
  bookCategoriesStatus: Status
  bookCategories: Category[]
  contributorsStatus: Status
  contributors: Contributor[]
  chapterContributorsStatus: Status
  chapterContributors: Contributor[]
  createContributorStatus: Status
  updateContributorStatus: Status
  deleteContributorStatus: Status
  activeContributorStatus: Status
  activeContributor: Contributor | null
  newlyCreatedContributorID: string | null
  publishChapterStatus: Status
  lastPublishedChapter: PublishChapterResponse | null
  unpublishChapterStatus: Status
  errorMessage: string
  successMessage: string
}

export let initialState: CodexState = {
  booksStatus: 'uninitialized',
  books: [],
  createBookStatus: 'uninitialized',
  updateBookStatus: 'uninitialized',
  deleteBookStatus: 'uninitialized',
  republishBookStatus: 'uninitialized',
  activeBookStatus: 'uninitialized',
  activeBook: null,
  newlyCreatedBookID: null,
  deletedBookID: null,
  chaptersStatus: 'uninitialized',
  chapters: [],
  categoriesStatus: 'uninitialized',
  categories: [],
  createCategoryStatus: 'uninitialized',
  newlyCreatedCategoryID: null,
  updateCategoryStatus: 'uninitialized',
  deleteCategoryStatus: 'uninitialized',
  bookCategoriesStatus: 'uninitialized',
  bookCategories: [],
  contributorsStatus: 'uninitialized',
  contributors: [],
  chapterContributorsStatus: 'uninitialized',
  chapterContributors: [],
  createContributorStatus: 'uninitialized',
  updateContributorStatus: 'uninitialized',
  deleteContributorStatus: 'uninitialized',
  activeContributorStatus: 'uninitialized',
  activeContributor: null,
  newlyCreatedContributorID: null,
  publishChapterStatus: 'uninitialized',
  lastPublishedChapter: null,
  unpublishChapterStatus: 'uninitialized',
  errorMessage: '',
  successMessage: '',
}

// Slice
export let codexSlice = _createSlice({
  name: 'codex',
  initialState,
  reducers: {
    errorMessageChanged: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
    successMessageChanged: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload
    },
    activeBookCleared: (state) => {
      state.activeBook = null
      state.activeBookStatus = 'uninitialized'
    },
    newlyCreatedBookIDCleared: (state) => {
      state.newlyCreatedBookID = null
    },
    deletedBookIDCleared: (state) => {
      state.deletedBookID = null
    },
    activeContributorCleared: (state) => {
      state.activeContributor = null
      state.activeContributorStatus = 'uninitialized'
    },
    newlyCreatedContributorIDCleared: (state) => {
      state.newlyCreatedContributorID = null
    },
    newlyCreatedCategoryIDCleared: (state) => {
      state.newlyCreatedCategoryID = null
    },
    chapterContributorsCleared: (state) => {
      state.chapterContributors = []
      state.chapterContributorsStatus = 'uninitialized'
    },
    chapterPublishStatusCleared: (state) => {
      state.publishChapterStatus = 'uninitialized'
      state.lastPublishedChapter = null
      state.unpublishChapterStatus = 'uninitialized'
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Books
      .addCase(fetchBooks.pending, (state) => {
        state.booksStatus = 'pending'
      })
      .addCase(fetchBooks.rejected, (state) => {
        state.booksStatus = 'rejected'
      })
      .addCase(fetchBooks.fulfilled, (state, action: PayloadAction<Book[]>) => {
        state.booksStatus = 'fulfilled'
        state.books = action.payload
      })
      // Fetch Book By ID
      .addCase(fetchBookByID.pending, (state) => {
        state.activeBookStatus = 'pending'
      })
      .addCase(fetchBookByID.rejected, (state) => {
        state.activeBookStatus = 'rejected'
      })
      .addCase(
        fetchBookByID.fulfilled,
        (state, action: PayloadAction<Book>) => {
          state.activeBookStatus = 'fulfilled'
          state.activeBook = action.payload
        },
      )
      // Create Book
      .addCase(createBook.pending, (state) => {
        state.createBookStatus = 'pending'
        state.newlyCreatedBookID = null
      })
      .addCase(createBook.fulfilled, (state, action: PayloadAction<Book>) => {
        state.createBookStatus = 'fulfilled'
        state.newlyCreatedBookID = action.payload.id
        state.successMessage = 'Book created successfully'
      })
      .addCase(createBook.rejected, (state) => {
        state.createBookStatus = 'rejected'
        state.errorMessage = 'Failed to create book'
      })
      // Update Book
      .addCase(updateBook.pending, (state) => {
        state.updateBookStatus = 'pending'
      })
      .addCase(updateBook.fulfilled, (state, action: PayloadAction<Book>) => {
        state.updateBookStatus = 'fulfilled'
        if (state.activeBook?.id === action.payload.id) {
          state.activeBook = action.payload
        }
        state.successMessage = 'Book updated successfully'
      })
      .addCase(updateBook.rejected, (state) => {
        state.updateBookStatus = 'rejected'
        state.errorMessage = 'Failed to update book'
      })
      // Update Publication Status
      .addCase(
        updatePublicationStatus.fulfilled,
        (state, action: PayloadAction<Book>) => {
          let index = state.books.findIndex((b) => b.id === action.payload.id)
          if (index !== -1) {
            state.books[index] = action.payload
          }
          if (state.activeBook?.id === action.payload.id) {
            state.activeBook = action.payload
          }
          state.successMessage = 'Publication status updated successfully'
        },
      )
      .addCase(updatePublicationStatus.rejected, (state) => {
        state.errorMessage = 'Failed to update publication status'
      })
      // Delete Book
      .addCase(deleteBook.pending, (state) => {
        state.deleteBookStatus = 'pending'
        state.deletedBookID = null
      })
      .addCase(deleteBook.fulfilled, (state, action: PayloadAction<string>) => {
        state.deleteBookStatus = 'fulfilled'
        state.deletedBookID = action.payload
        state.successMessage = 'Book deleted successfully'
      })
      .addCase(deleteBook.rejected, (state) => {
        state.deleteBookStatus = 'rejected'
        state.errorMessage = 'Failed to delete book'
      })
      // Republish Book
      .addCase(republishBook.pending, (state) => {
        state.republishBookStatus = 'pending'
      })
      .addCase(republishBook.fulfilled, (state) => {
        state.republishBookStatus = 'fulfilled'
        state.successMessage = 'Book republished successfully'
      })
      .addCase(republishBook.rejected, (state) => {
        state.republishBookStatus = 'rejected'
        state.errorMessage = 'Failed to republish book'
      })
      // Fetch Book Chapters
      .addCase(fetchBookChapters.pending, (state) => {
        state.chaptersStatus = 'pending'
      })
      .addCase(fetchBookChapters.rejected, (state) => {
        state.chaptersStatus = 'rejected'
      })
      .addCase(
        fetchBookChapters.fulfilled,
        (state, action: PayloadAction<Chapter[]>) => {
          state.chaptersStatus = 'fulfilled'
          state.chapters = action.payload
        },
      )
      // Create Chapter
      .addCase(
        createChapter.fulfilled,
        (state, action: PayloadAction<Chapter>) => {
          state.chapters.push(action.payload)
          state.successMessage = 'Chapter created successfully'
        },
      )
      .addCase(createChapter.rejected, (state) => {
        state.errorMessage = 'Failed to create chapter'
      })
      // Update Chapter
      .addCase(
        updateChapter.fulfilled,
        (state, action: PayloadAction<Chapter>) => {
          let index = state.chapters.findIndex(
            (c) => c.id === action.payload.id,
          )
          if (index !== -1) {
            state.chapters[index] = action.payload
          }
          state.successMessage = 'Chapter updated successfully'
        },
      )
      .addCase(updateChapter.rejected, (state) => {
        state.errorMessage = 'Failed to update chapter'
      })
      // Delete Chapter
      .addCase(deleteChapter.fulfilled, (state) => {
        state.successMessage = 'Chapter deleted successfully'
      })
      .addCase(deleteChapter.rejected, (state) => {
        state.errorMessage = 'Failed to delete chapter'
      })
      // Reorder Chapters
      .addCase(reorderChapters.fulfilled, (state) => {
        state.successMessage = 'Chapters reordered successfully'
      })
      .addCase(reorderChapters.rejected, (state) => {
        state.errorMessage = 'Failed to reorder chapters'
      })
      // Fetch Categories
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesStatus = 'pending'
      })
      .addCase(fetchCategories.rejected, (state) => {
        state.categoriesStatus = 'rejected'
      })
      .addCase(
        fetchCategories.fulfilled,
        (state, action: PayloadAction<Category[]>) => {
          state.categoriesStatus = 'fulfilled'
          state.categories = action.payload.toSorted(
            (a, b) => a.sequence - b.sequence,
          )
        },
      )
      // Create Category
      .addCase(createCategory.pending, (state) => {
        state.createCategoryStatus = 'pending'
        state.newlyCreatedCategoryID = null
      })
      .addCase(
        createCategory.fulfilled,
        (state, action: PayloadAction<Category>) => {
          state.createCategoryStatus = 'fulfilled'
          state.newlyCreatedCategoryID = action.payload.id
          state.categories = [...state.categories, action.payload].toSorted(
            (a, b) => a.sequence - b.sequence,
          )
          state.successMessage = 'Category created successfully'
        },
      )
      .addCase(createCategory.rejected, (state) => {
        state.createCategoryStatus = 'rejected'
        state.errorMessage = 'Failed to create category'
      })
      // Update Category
      .addCase(updateCategory.pending, (state) => {
        state.updateCategoryStatus = 'pending'
      })
      .addCase(
        updateCategory.fulfilled,
        (state, action: PayloadAction<Category>) => {
          state.updateCategoryStatus = 'fulfilled'
          let index = state.categories.findIndex(
            (c) => c.id === action.payload.id,
          )
          if (index !== -1) {
            state.categories[index] = action.payload
          }
          state.categories = state.categories.toSorted(
            (a, b) => a.sequence - b.sequence,
          )
          state.successMessage = 'Category updated successfully'
        },
      )
      .addCase(updateCategory.rejected, (state) => {
        state.updateCategoryStatus = 'rejected'
        state.errorMessage = 'Failed to update category'
      })
      // Delete Category
      .addCase(deleteCategory.pending, (state) => {
        state.deleteCategoryStatus = 'pending'
      })
      .addCase(deleteCategory.fulfilled, (state) => {
        state.deleteCategoryStatus = 'fulfilled'
        state.successMessage = 'Category deleted successfully'
      })
      .addCase(deleteCategory.rejected, (state) => {
        state.deleteCategoryStatus = 'rejected'
        state.errorMessage = 'Failed to delete category'
      })
      // Fetch Book Categories
      .addCase(fetchBookCategories.pending, (state) => {
        state.bookCategoriesStatus = 'pending'
      })
      .addCase(fetchBookCategories.rejected, (state) => {
        state.bookCategoriesStatus = 'rejected'
      })
      .addCase(
        fetchBookCategories.fulfilled,
        (state, action: PayloadAction<Category[]>) => {
          state.bookCategoriesStatus = 'fulfilled'
          state.bookCategories = action.payload
        },
      )
      // Assign Category to Book
      .addCase(
        assignCategoryToBook.fulfilled,
        (state, action: PayloadAction<BookCategoryAssociation>) => {
          state.successMessage = 'Category assigned successfully'
          // Server replaces any prior assignment for this book, so mirror
          // that locally instead of waiting on a refetch.
          let assigned = state.categories.find(
            (c) => c.id === action.payload.codexCategoryID,
          )
          state.bookCategories = assigned ? [assigned] : []
        },
      )
      .addCase(assignCategoryToBook.rejected, (state) => {
        state.errorMessage = 'Failed to assign category'
      })
      // Remove Category from Book
      .addCase(
        removeCategoryFromBook.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.successMessage = 'Category removed successfully'
          state.bookCategories = state.bookCategories.filter(
            (c) => c.id !== action.payload,
          )
        },
      )
      .addCase(removeCategoryFromBook.rejected, (state) => {
        state.errorMessage = 'Failed to remove category'
      })
      // Fetch Contributors
      .addCase(fetchContributors.pending, (state) => {
        state.contributorsStatus = 'pending'
      })
      .addCase(fetchContributors.rejected, (state) => {
        state.contributorsStatus = 'rejected'
      })
      .addCase(
        fetchContributors.fulfilled,
        (state, action: PayloadAction<Contributor[]>) => {
          state.contributorsStatus = 'fulfilled'
          state.contributors = action.payload
        },
      )
      // Fetch Contributor By ID
      .addCase(fetchContributorByID.pending, (state) => {
        state.activeContributorStatus = 'pending'
      })
      .addCase(fetchContributorByID.rejected, (state) => {
        state.activeContributorStatus = 'rejected'
      })
      .addCase(
        fetchContributorByID.fulfilled,
        (state, action: PayloadAction<Contributor>) => {
          state.activeContributorStatus = 'fulfilled'
          state.activeContributor = action.payload
        },
      )
      // Create Contributor
      .addCase(createContributor.pending, (state) => {
        state.createContributorStatus = 'pending'
        state.newlyCreatedContributorID = null
      })
      .addCase(
        createContributor.fulfilled,
        (state, action: PayloadAction<Contributor>) => {
          state.createContributorStatus = 'fulfilled'
          state.newlyCreatedContributorID = action.payload.id
          state.contributors.push(action.payload)
          state.successMessage = 'Contributor created successfully'
        },
      )
      .addCase(createContributor.rejected, (state) => {
        state.createContributorStatus = 'rejected'
        state.errorMessage = 'Failed to create contributor'
      })
      // Update Contributor
      .addCase(updateContributor.pending, (state) => {
        state.updateContributorStatus = 'pending'
      })
      .addCase(
        updateContributor.fulfilled,
        (state, action: PayloadAction<Contributor>) => {
          state.updateContributorStatus = 'fulfilled'
          let index = state.contributors.findIndex(
            (c) => c.id === action.payload.id,
          )
          if (index !== -1) {
            state.contributors[index] = action.payload
          }
          if (state.activeContributor?.id === action.payload.id) {
            state.activeContributor = action.payload
          }
          state.successMessage = 'Contributor updated successfully'
        },
      )
      .addCase(updateContributor.rejected, (state) => {
        state.updateContributorStatus = 'rejected'
        state.errorMessage = 'Failed to update contributor'
      })
      // Delete Contributor
      .addCase(deleteContributor.pending, (state) => {
        state.deleteContributorStatus = 'pending'
      })
      .addCase(deleteContributor.fulfilled, (state) => {
        state.deleteContributorStatus = 'fulfilled'
        state.successMessage = 'Contributor deleted successfully'
      })
      .addCase(deleteContributor.rejected, (state) => {
        state.deleteContributorStatus = 'rejected'
        state.errorMessage = 'Failed to delete contributor'
      })
      // Fetch Chapter Contributors
      .addCase(fetchChapterContributors.pending, (state) => {
        state.chapterContributorsStatus = 'pending'
      })
      .addCase(fetchChapterContributors.rejected, (state) => {
        state.chapterContributorsStatus = 'rejected'
      })
      .addCase(
        fetchChapterContributors.fulfilled,
        (state, action: PayloadAction<Contributor[]>) => {
          state.chapterContributorsStatus = 'fulfilled'
          state.chapterContributors = action.payload
        },
      )
      // Assign Contributor to Chapter
      .addCase(assignContributorToChapter.fulfilled, (state) => {
        state.successMessage = 'Contributor assigned successfully'
      })
      .addCase(assignContributorToChapter.rejected, (state) => {
        state.errorMessage = 'Failed to assign contributor'
      })
      // Remove Contributor from Chapter
      .addCase(removeContributorFromChapter.fulfilled, (state) => {
        state.successMessage = 'Contributor removed successfully'
      })
      .addCase(removeContributorFromChapter.rejected, (state) => {
        state.errorMessage = 'Failed to remove contributor'
      })
      // Publish Chapter
      .addCase(publishChapter.pending, (state) => {
        state.publishChapterStatus = 'pending'
        state.lastPublishedChapter = null
      })
      .addCase(
        publishChapter.fulfilled,
        (state, action: PayloadAction<PublishChapterResponse>) => {
          state.publishChapterStatus = 'fulfilled'
          state.lastPublishedChapter = action.payload
          state.unpublishChapterStatus = 'uninitialized'
        },
      )
      .addCase(publishChapter.rejected, (state) => {
        state.publishChapterStatus = 'rejected'
        state.errorMessage = 'Failed to publish chapter'
      })
      // Unpublish Chapter
      .addCase(unpublishChapter.pending, (state) => {
        state.unpublishChapterStatus = 'pending'
      })
      .addCase(unpublishChapter.fulfilled, (state) => {
        state.unpublishChapterStatus = 'fulfilled'
        state.lastPublishedChapter = null
        state.publishChapterStatus = 'uninitialized'
      })
      .addCase(unpublishChapter.rejected, (state) => {
        state.unpublishChapterStatus = 'rejected'
        state.errorMessage = 'Failed to unpublish chapter'
      })
  },
})

export let {
  errorMessageChanged,
  successMessageChanged,
  activeBookCleared,
  newlyCreatedBookIDCleared,
  deletedBookIDCleared,
  activeContributorCleared,
  newlyCreatedContributorIDCleared,
  newlyCreatedCategoryIDCleared,
  chapterContributorsCleared,
  chapterPublishStatusCleared,
} = codexSlice.actions

// Selectors
export let selectBooksStatus = (state: AppRootState) => state.codex.booksStatus
export let selectBooks = (state: AppRootState) => state.codex.books
export let selectCreateBookStatus = (state: AppRootState) =>
  state.codex.createBookStatus
export let selectUpdateBookStatus = (state: AppRootState) =>
  state.codex.updateBookStatus
export let selectDeleteBookStatus = (state: AppRootState) =>
  state.codex.deleteBookStatus
export let selectRepublishBookStatus = (state: AppRootState) =>
  state.codex.republishBookStatus
export let selectActiveBookStatus = (state: AppRootState) =>
  state.codex.activeBookStatus
export let selectActiveBook = (state: AppRootState) => state.codex.activeBook
export let selectNewlyCreatedBookID = (state: AppRootState) =>
  state.codex.newlyCreatedBookID
export let selectDeletedBookID = (state: AppRootState) =>
  state.codex.deletedBookID
export let selectChaptersStatus = (state: AppRootState) =>
  state.codex.chaptersStatus
export let selectChapters = (state: AppRootState) => state.codex.chapters
export let selectCategoriesStatus = (state: AppRootState) =>
  state.codex.categoriesStatus
export let selectCategories = (state: AppRootState) => state.codex.categories
export let selectCreateCategoryStatus = (state: AppRootState) =>
  state.codex.createCategoryStatus
export let selectNewlyCreatedCategoryID = (state: AppRootState) =>
  state.codex.newlyCreatedCategoryID
export let selectUpdateCategoryStatus = (state: AppRootState) =>
  state.codex.updateCategoryStatus
export let selectDeleteCategoryStatus = (state: AppRootState) =>
  state.codex.deleteCategoryStatus
export let selectBookCategoriesStatus = (state: AppRootState) =>
  state.codex.bookCategoriesStatus
export let selectBookCategories = (state: AppRootState) =>
  state.codex.bookCategories
export let selectErrorMessage = (state: AppRootState) =>
  state.codex.errorMessage
export let selectSuccessMessage = (state: AppRootState) =>
  state.codex.successMessage
export let selectContributorsStatus = (state: AppRootState) =>
  state.codex.contributorsStatus
export let selectContributors = (state: AppRootState) =>
  state.codex.contributors
export let selectCreateContributorStatus = (state: AppRootState) =>
  state.codex.createContributorStatus
export let selectUpdateContributorStatus = (state: AppRootState) =>
  state.codex.updateContributorStatus
export let selectDeleteContributorStatus = (state: AppRootState) =>
  state.codex.deleteContributorStatus
export let selectActiveContributorStatus = (state: AppRootState) =>
  state.codex.activeContributorStatus
export let selectActiveContributor = (state: AppRootState) =>
  state.codex.activeContributor
export let selectNewlyCreatedContributorID = (state: AppRootState) =>
  state.codex.newlyCreatedContributorID
export let selectChapterContributorsStatus = (state: AppRootState) =>
  state.codex.chapterContributorsStatus
export let selectChapterContributors = (state: AppRootState) =>
  state.codex.chapterContributors
export let selectPublishChapterStatus = (state: AppRootState) =>
  state.codex.publishChapterStatus
export let selectLastPublishedChapter = (state: AppRootState) =>
  state.codex.lastPublishedChapter
export let selectUnpublishChapterStatus = (state: AppRootState) =>
  state.codex.unpublishChapterStatus

export default codexSlice.reducer
